import { setSelectedTheme, setSelectedThemeSwitcherState } from '../../Store/features/themes/themesSlice';
import { useEffect, useState } from 'react';
import CheckIcon from '../Utility/CheckIcon';
import { RootState, useAppDispatch, useAppSelector } from '../../store';

export default function ThemeSwitcher() {
    const themes = useAppSelector((state: RootState) => (state as any).themes?.allThemes || {});
    const version = useAppSelector((state: RootState) => state.settings.version);
    const themeSwitcherState = useAppSelector((state: RootState) => (state as any).themes?.themeSwitcherState || '');
    const dispatch = useAppDispatch();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const [lightTheme, setLightTheme] = useState<any>(null);
    const [darkTheme, setDarkTheme] = useState<any>(null);

    useEffect(() => {
        const themeValues = Object.values(themes);
        setLightTheme(themeValues.find((theme: any) => theme.system === 'light'));
        setDarkTheme(themeValues.find((theme: any) => theme.system === 'dark'));
    }, [themes]);

    const switchTheme = (theme: string) => {
        if (theme === 'system') {
            let systemTheme = Object.keys(themes)[0];
            const browserMode =
                window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            Object.keys(themes).forEach((key: any) => {
                if (themes[key].system === browserMode) {
                    systemTheme = key;
                }
            });
            const linkElement = document.querySelector('#themeElement');
            linkElement?.setAttribute('href', '/themes/css/' + systemTheme + '.css?' + version);
            dispatch(setSelectedTheme(systemTheme));
            dispatch(setSelectedThemeSwitcherState('system'));
        } else {
            const linkElement = document.querySelector('#themeElement');
            linkElement?.setAttribute('href', '/themes/css/' + theme + '.css?' + version);
            dispatch(setSelectedTheme(theme));
            dispatch(setSelectedThemeSwitcherState(theme));
        }
    };

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 border-0 pt-4">
            {Object.keys(themes).map((theme: any) => {
                return (
                    <div
                        key={theme}
                        className={`flex flex-col p-0  cursor-pointer`}
                        onClick={() => {
                            switchTheme(theme);
                        }}
                        onTouchStart={() => {
                            switchTheme(theme);
                        }}
                    >
                        <div
                            className={`aspect-[3/2] border-[3px] rounded-xl transition-all duration-100 cursor-pointer relative overflow-hidden ${
                                themeSwitcherState === theme
                                    ? 'border-primary'
                                    : 'border-layout-borderColor hover:border-primary'
                            }`}
                        >
                            {themeSwitcherState === theme && <CheckIcon />}

                            <div
                                className={`absolute m-auto top-0 h-full w-full cursor-pointer`}
                                style={{
                                    backgroundImage: `url(${themes[theme].preview}?${version})`,
                                    backgroundPosition: 'center',
                                    backgroundSize: 'cover',
                                    backgroundRepeat: 'no-repeat',
                                }}
                            ></div>
                        </div>
                        <div
                            className={`flex justify-center items-center gap-2 pt-2 text-layout-textcolor
                                    `}
                        >
                            <p className="w-fit text-center p-0 m-0 line-clamp-1">{themes[theme].title}</p>
                        </div>
                    </div>
                );
            })}
            {themes.length !== 1 && themes !== null && (
                <div
                    className={`flex flex-col p-0  cursor-pointer`}
                    onClick={() => {
                        switchTheme('system');
                    }}
                    onTouchStart={() => {
                        switchTheme('system');
                    }}
                >
                    <div
                        className={`aspect-[3/2] border-[3px] rounded-xl transition-all duration-100 cursor-pointer relative overflow-hidden ${
                            themeSwitcherState === 'system'
                                ? 'border-primary'
                                : 'border-layout-borderColor hover:border-primary'
                        }`}
                    >
                        {themeSwitcherState === 'system' && <CheckIcon />}

                        {darkTheme && darkTheme.preview && (
                            <div
                                className={`absolute m-auto top-0 left-0 h-full w-full transition-all duration-100 cursor-pointer`}
                                style={{
                                    backgroundImage: `url(${darkTheme.preview}?${version})`,
                                    backgroundPosition: 'center',
                                    backgroundSize: 'cover',
                                    backgroundRepeat: 'no-repeat',
                                    clipPath: 'polygon(0 0, 100% 0, 50% 50%, 0 100%)',
                                }}
                            ></div>
                        )}
                        {lightTheme && lightTheme.preview && (
                            <div
                                className={`absolute m-auto top-0 right-0 h-full w-full transition-all duration-100 cursor-pointer`}
                                style={{
                                    backgroundImage: `url(${lightTheme.preview}?${version})`,
                                    backgroundPosition: 'center',
                                    backgroundSize: 'cover',
                                    backgroundRepeat: 'no-repeat',
                                    clipPath: 'polygon(50% 50%, 100% 0, 100% 100%, 0 100%)',
                                }}
                            ></div>
                        )}
                    </div>
                    <div className={`flex justify-center items-center gap-2 pt-2 text-layout-textcolor `}>
                        <p className="w-fit text-center p-0 m-0 line-clamp-1">
                            {i18n.translate({
                                id: 'themeswitcher.system',
                                sourceName: 'ThemeSwitcher',
                            })}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
